import { useAccountType, usePageTitle } from '@src/contexts';
import { DashboardParams } from '@src/services/dashboard/dashboardTypes';
import { useLazyGetSimLocationsQuery } from '@src/services/dashboard/simLocations/simLocationsApiSlice';
import { useLazyGetSimsInfoQuery } from '@src/services/dashboard/simsInfo/simsInfoApiSlice';
import { useLazyGetSubAccountsQuery } from '@src/services/dashboard/subAccounts/subAccountsApiSlice';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Card,
    Charts,
    Filter,
    MyAccountEmptyState,
    SubAccounts,
    SubAccountsEmptyState,
    TopCountries,
    WorldMap,
} from './components';
import { IdentificationIcon, MapPinIcon } from '@heroicons/react/24/solid';

export function Dashboard() {
    const { t } = useTranslation();
    const { setBreadcrumbs } = usePageTitle();
    const { setShowAccountTypeSelector, accountType } = useAccountType();

    useEffect(() => {
        setShowAccountTypeSelector(true);
        return () => {
            setShowAccountTypeSelector(false); // Clean up when leaving page
        };
    }, [setShowAccountTypeSelector]);

    useEffect(() => {
        setBreadcrumbs([{ title: t('pages.dashboard.title') }]);
    }, []);

    const [getSimsInfo, { data: dataSimsInfo, isLoading: isLoadingSimsInfo }] =
        useLazyGetSimsInfoQuery();
    const [
        getSubAccounts,
        { data: dataSubAccounts, isLoading: isLoadingSubAccounts },
    ] = useLazyGetSubAccountsQuery();

    const [
        getSimLocations,
        { data: dataSimLocations, isLoading: isLoadingSimLocations },
    ] = useLazyGetSimLocationsQuery();
    const [filter, setFilter] = useState<DashboardParams>({});

    const handleAccountsLoaded = (ids: string[]) => {
        setFilter((prev) => {
            if (!prev.subAccountIds?.length) {
                return { ...prev, subAccountIds: ids };
            }
            return prev;
        });
    };

    useEffect(() => {
        getInfo();
    }, [filter]);

    const getInfo = () => {
        getSimsInfo(filter);
        getSimLocations(filter);
        getSubAccounts({});
    };

    useEffect(() => {
        setFilter({});
    }, [accountType]);

    return (
        <div>
            <div className="mb-3">
                <Filter
                    accountType={accountType}
                    setFilter={setFilter}
                    onAccountsLoaded={handleAccountsLoaded}
                />
            </div>
            <Card
                title={`${accountType === 'myAccount' || !filter?.subAccountIds?.length ? dataSubAccounts?.data?.total || 0 : filter?.subAccountIds?.length ? (filter?.subAccountIds?.length === 1 ? filter.subAccountName : filter.subAccountIds.length) : 0}${filter?.subAccountIds?.length === 1 && accountType !== 'myAccount' ? '' : ' Sub-Accounts'}`}
                icon={
                    <IdentificationIcon className="w-6 h-6 text-primary opacity-45" />
                }
                content={
                    <div className="mx-2 md:mx-4">
                        <SubAccounts data={dataSimsInfo?.data} />
                    </div>
                }
            />
            {dataSimsInfo && dataSimsInfo.data.activeInstalled > 0 && (
                <Charts t={t} reportType={accountType} filter={filter} />
            )}

            {dataSimsInfo &&
                dataSimsInfo.data.activeInstalled > 0 &&
                accountType === 'myAccount' && (
                    <Card
                        title="SIM per Location"
                        icon={
                            <MapPinIcon className="w-6 h-6 text-primary opacity-45" />
                        }
                        content={
                            <div className="flex flex-col md:flex-row gap-8 mx-2">
                                <div className="w-full md:w-3/5">
                                    <WorldMap
                                        countries={dataSimLocations?.data?.filter(
                                            (country) => country.total > 0
                                        )}
                                    />
                                </div>
                                <div className="w-full md:w-2/5">
                                    <TopCountries
                                        countryList={dataSimLocations?.data}
                                        loading={isLoadingSimLocations}
                                    />
                                </div>
                            </div>
                        }
                    />
                )}

            {dataSimsInfo &&
                dataSimsInfo.data.activeInstalled === 0 &&
                (accountType === 'myAccount' ? (
                    <MyAccountEmptyState t={t} className="mt-8" />
                ) : (
                    <SubAccountsEmptyState t={t} />
                ))}
        </div>
    );
}
